/* lib/adapters/reports.js — `reportsReal`: тот же сёрфейс, что у fakeApi (listReports/getReport/
 * deleteReport), но поверх боевого reports-клиента. Через unwrap() (throw{status}) — useFakeQuery
 * и catch-ветки работают без правок. Главная работа — per-type result-адаптеры: pydantic-результат
 * скилла (reports/schemas.py) → форма, которую читают рендереры ReportDetail.
 *
 * Тип отчёта на бэке — короткий slug ('foodcost'|'abc'|'inventory');
 * рендереры (renderBody) и фильтры переведены на него же. Текстовый поиск по сущности backend
 * не поддерживает (только subject_kind+subject_id) → free-text фильтруем client-side по title/summary. */

import { unwrap } from '../api/unwrap.js';
import * as api from '../api/reports.js';

const num = (v) => (v == null ? null : Number(v));

const asArray = (d) => (Array.isArray(d) ? d : (d && d.results) || []);

function adaptBrief(r) {
  return {
    id: r.id, type: r.type, type_label: r.type_label, title: r.title,
    summary: r.summary || '', created_at: r.created_at, created_by_email: r.created_by_email || '',
    subject_kind: r.subject_kind, subject_kind_label: r.subject_kind_label, subject_id: r.subject_id,
  };
}

// ── foodcost-optimize: FoodcostOptimizeResponse → рендерер FoodcostReport ──
function adaptFoodcost(res) {
  const measures = res.measures || [];
  const final = res.final || null;
  // Потенциал экономии бэк отдельным полем не отдаёт → выводим из current/final.
  const potential = final ? (Number(res.current_cost_rub || 0) - Number(final.new_cost_rub || 0)) : 0;
  const v = res.verdict || null; // {safest_index,balanced_index,max_effect_index} — индексы в measures
  const recFor = (idx, label) => {
    const m = measures[idx];
    return m ? { label, total_savings_rub: num(m.savings_rub), measures_ids: [m.title] } : null;
  };
  const recommendations = v
    ? [recFor(v.safest_index, 'Безопасный'), recFor(v.balanced_index, 'Компромисс'), recFor(v.max_effect_index, 'Максимум эффекта')].filter(Boolean)
    : [];
  return {
    metrics: {
      current_fc_pct: num(res.current_fc_pct), target_fc_pct: num(res.target_fc_pct),
      potential_savings_rub: potential,
    },
    cost_drivers: (res.cost_drivers || []).map((d) => ({
      ingredient_name: d.name, cost_rub: num(d.contribution_rub), share_pct: num(d.contribution_pct), note: d.note,
    })),
    // before/after — СТРОКИ (не числа), savings — числа; рендерер показывает их как есть.
    measures: measures.map((m) => ({
      type: m.type, risk: m.risk, title: m.title, before: m.before, after: m.after,
      compensation: m.compensation, savings_rub: num(m.savings_rub), savings_pct: num(m.savings_pct),
      justification: m.justification,
    })),
    recommendations,
    verdict: res.assistant_summary || '', // строка для финального callout
  };
}

// ── abc-analyze: ABCAnalyzeResponse → рендерер AbcReport ──
function adaptAbc(res) {
  const positions = res.positions || [];
  const count = (p) => positions.filter((x) => x.priority === p).length;
  return {
    // metrics нет в схеме — считаем счётчики из positions[].priority (LLM извлёк, код считает).
    metrics: { p1_count: count('p1'), p2_count: count('p2'), p3_count: count('p3'), potential_count: count('potential') },
    // priority/fc_status отдаём как есть — PriorityTag/FcStatusTag расширены под backend-значения.
    items: positions.map((p) => ({
      dish_name: p.name, abc_code: p.abc_code, fc_pct: num(p.fc_pct), fc_status: p.fc_status,
      priority: p.priority, comment: p.comment,
    })),
    insufficient_data: (res.missing_data || []).map((x) => ({ dish_name: x.name, reason: x.reason })),
  };
}

// ── inventory-analyze: InventoryAnalyzeResponse → рендерер InventoryReport ──
const GROUP_LABELS = {
  missort: 'Пересорт', purchase_errors: 'Ошибки прихода', ttk: 'ТТК / % отхода',
  units: 'Единицы измерения', writeoffs: 'Списания / брак', bar: 'Бар', recount: 'Точечный пересчёт',
};
function adaptInventory(res) {
  const months = res.months || [];
  const revenue = res.revenue || [];
  const shortages = res.top_shortages || [];
  const surpluses = res.top_surpluses || [];
  const sumAbs = (arr) => arr.reduce((s, x) => s + Math.abs(Number(x.delta_rub) || 0), 0);
  const g = res.groups || {};
  const groups = Object.entries(GROUP_LABELS)
    .map(([k, label]) => ({ cause: label, count: g[k] || 0 }))
    .filter((x) => x.count > 0);
  const mapItem = (x) => ({ ingredient: x.name, delta_units: num(x.delta_units), unit: x.unit, delta_rub: num(x.delta_rub), trend: x.trend });
  const sc = res.scenarios || null;
  const mapScenario = (s, label) => (s ? {
    label, shortage_after_rub: num(s.shortage_after_rub), surplus_after_rub: num(s.surplus_after_rub), deviation_pct: num(s.deviation_pct),
  } : null);
  return {
    metrics: {
      months_count: months.length,
      revenue_total: revenue.length ? Number(revenue[revenue.length - 1]) : null,
      shortage_total_rub: sumAbs(shortages),
      surplus_total_rub: sumAbs(surpluses),
    },
    groups,
    top_shortages: shortages.map(mapItem),
    top_surpluses: surpluses.map(mapItem),
    diagnostics: (res.diagnoses || []).map((d) => ({
      ingredient: d.item, status: d.status, effect_rub: num(d.potential_effect_rub),
      criteria: d.criteria || [], recommendation: d.recommendation, diagnosis: d.diagnosis, data_needed: d.data_needed,
    })),
    action_plan: (res.top_actions || []).map((a) => (
      a.expected_effect_rub ? `${a.action} — эффект ${Math.round(Number(a.expected_effect_rub))} ₽` : a.action
    )),
    scenarios: sc ? { conservative: mapScenario(sc.conservative, 'Консервативный'), target: mapScenario(sc.target, 'Целевой') } : null,
  };
}

const RESULT_ADAPTERS = { foodcost: adaptFoodcost, abc: adaptAbc, inventory: adaptInventory };

// ── F40 W3b: report-JSON (_schema:'f40') → ТА ЖЕ рендерер-форма (переиспользуем
// существующие рендереры). Дискриминатор result._schema ветвит adaptDetail;
// старые отчёты (без _schema) идут прежним адаптером выше. foodcost — W3b-1;
// abc/inventory — W3b-2.
function adaptF40Foodcost(res) {
  const kb = res.key_breakdown || {};
  const fe = res.final_effect || {};
  const cons = res.constraints || {};
  const recs = res.recommendations || [];
  const sc = res.scenarios || {};
  const targetFc = (cons.goal === 'target_fc' && cons.goal_value != null) ? num(cons.goal_value) : null;
  const potential = (kb.current_cost != null && fe.estimated_cost != null)
    ? Number(kb.current_cost) - Number(fe.estimated_cost) : 0;
  const recById = {};
  for (const r of recs) recById[r.id] = r.target_ingredient || r.measure_type || r.id;
  const scenRec = (s, label) => (s ? {
    label, total_savings_rub: num(s.est_total_savings_rub),
    measures_ids: (s.rec_ids || []).map((id) => recById[id] || id),
  } : null);
  return {
    metrics: { current_fc_pct: num(kb.current_fc_pct), target_fc_pct: targetFc, potential_savings_rub: potential },
    cost_drivers: (kb.cost_drivers || []).map((d) => ({
      ingredient_name: d.ingredient, cost_rub: num(d.cost), share_pct: num(d.share_pct), note: '',
    })),
    // est_* — оценки (помечены в самом отчёте); рендерер показывает before/after-строки и числа как есть.
    measures: recs.map((r) => ({
      type: r.measure_type, risk: r.risk, title: r.target_ingredient || r.before || r.id,
      before: r.before, after: r.after, compensation: r.compensation,
      savings_rub: num(r.est_savings_rub), savings_pct: num(r.est_savings_pct), justification: r.risk_note,
    })),
    recommendations: [scenRec(sc.best_safe, 'Безопасный'), scenRec(sc.compromise, 'Компромисс'), scenRec(sc.best_effect, 'Максимум эффекта')].filter(Boolean),
    verdict: res.summary || '',
  };
}

// F40 abc → форма adaptAbc. priority enum F40 (critical/warning/growth/ok) →
// старый (p1/p2/p3/potential/normal), fc_status выводим из fc (шкала продукта).
const _F40_ABC_PRIO = { critical: 'p1', warning: 'p2', growth: 'potential', ok: 'normal' };
function _fcStatus(fc) {
  if (fc == null || fc === 0) return 'no_data';
  if (fc < 20) return 'good';
  if (fc < 25) return 'above_target';
  if (fc < 30) return 'critical';
  return 'urgent';
}
function adaptF40Abc(res) {
  const items = res.items || [];
  const prios = items.map((i) => _F40_ABC_PRIO[i.priority] || 'normal');
  const cnt = (p) => prios.filter((x) => x === p).length;
  return {
    metrics: { p1_count: cnt('p1'), p2_count: cnt('p2'), p3_count: cnt('p3'), potential_count: cnt('potential') },
    items: items.map((i) => {
      const fc = num(i.fc);
      return {
        dish_name: i.dish, abc_code: i.abc_combo, fc_pct: fc, fc_status: _fcStatus(fc),
        priority: _F40_ABC_PRIO[i.priority] || 'normal', comment: i.comment,
      };
    }),
    insufficient_data: (res.insufficient_data || []).map((x) => ({ dish_name: x.dish, reason: x.reason })),
  };
}

// F40 inventory → форма adaptInventory (best-effort: поля-агрегаты считаем из
// топов/cause_summary; сценарии F40 несут recoverable_rub/basis — старые поля
// after-баланса отдаём null, чтобы рендерер не падал).
function adaptF40Inventory(res) {
  const mapLine = (x) => ({ ingredient: x.item, delta_units: num(x.delta_qty), unit: x.unit, delta_rub: num(x.delta_rub), trend: 'flat' });
  const sumAbs = (arr) => (arr || []).reduce((s, x) => s + Math.abs(Number(x.delta_rub) || 0), 0);
  const pe = res.projected_effect || {};
  const mapScen = (s, label) => (s ? {
    label, shortage_after_rub: null, surplus_after_rub: null, deviation_pct: null,
    recoverable_rub: num(s.recoverable_rub), basis: s.basis || '',
  } : null);
  return {
    metrics: {
      months_count: null, revenue_total: num((pe.current_loss_rub != null) ? null : null),
      shortage_total_rub: sumAbs(res.top_shortage), surplus_total_rub: sumAbs(res.top_surplus),
    },
    groups: (res.cause_summary || []).filter((c) => (c.items || 0) > 0).map((c) => ({ cause: c.cause, count: c.items })),
    top_shortages: (res.top_shortage || []).map(mapLine),
    top_surpluses: (res.top_surplus || []).map(mapLine),
    diagnostics: (res.diagnostics || []).map((d) => ({
      ingredient: d.item, status: d.status, effect_rub: num(d.potential_effect_rub),
      criteria: d.criteria || [], recommendation: d.recommendation, diagnosis: d.diagnosis,
      data_needed: (d.data_needed || []).join('; '),
    })),
    action_plan: (res.action_plan || []).map((a) => (a.action || '')),
    scenarios: (pe.conservative || pe.target)
      ? { conservative: mapScen(pe.conservative, 'Консервативный'), target: mapScen(pe.target, 'Целевой') } : null,
  };
}

const F40_ADAPTERS = { foodcost: adaptF40Foodcost, abc: adaptF40Abc, inventory: adaptF40Inventory };

// ── v2-отчёты (schema_version: 2) — result собран КОДОМ на бэке
// (reports/inventory_calc.assemble_result / reports/abc_calc.assemble_result /
// reports/foodcost_calc.assemble_result), форма уже рендерная: passthrough +
// маркер v2 для renderBody (единые InventoryAnalysisReport / AbcAnalysisReport /
// FoodcostOptimizationReport). Реестры лейблов — lib/reports/
// inventoryCriteria.js / abcMeta.js / foodcostMeta.js.
const V2_KINDS = new Set(['inventory', 'abc', 'foodcost']);
function adaptF40V2(res) {
  return { v2: true, ...res };
}

function adaptDetail(d) {
  const raw = d.result || {};
  let result;
  if (raw._schema === 'f40' && raw.schema_version === 2 && V2_KINDS.has(raw.report_kind)) {
    result = adaptF40V2(raw);                      // v2 — новый рендерер
  } else if (raw._schema === 'f40' && F40_ADAPTERS[raw.report_kind]) {
    result = F40_ADAPTERS[raw.report_kind](raw);   // F40-путь (W3b, v1)
  } else {
    const adapt = RESULT_ADAPTERS[d.type];
    result = adapt ? adapt(raw) : raw;             // старые отчёты — прежний адаптер
  }
  return {
    id: d.id, type: d.type, type_label: d.type_label, title: d.title,
    summary: d.summary || '', created_at: d.created_at, created_by_email: d.created_by_email || '',
    subject_kind: d.subject_kind, subject_id: d.subject_id,
    // чат-источник: null = чат удалён или не виден юзеру → кнопки «Вернуться в чат» нет
    chat_id: d.chat_id ?? null,
    result,
    // СЫРОЙ result (схема-форма) — основа правки: редактор правит ИМЕННО его
    // (адаптер лоссит/переименовывает, обратно не инвертируется). PATCH собирается из него.
    result_raw: raw,
    // F40-результаты read-only: merge_editable_result на бэке валидирует
    // legacy-pydantic-схемой, f40-форма через неё не проходит.
    result_editable: raw._schema !== 'f40',
  };
}

// ── правка result: editable-surface (зеркало reports/services.EDITABLE_RESULT_FIELDS) ──
// Только авторская проза; вычисляемые числа НЕ редактируются (инвариант «LLM извлекает —
// код считает»). UI правит СЫРОЙ result (result_raw); toResultPatch вытаскивает editable-
// подмножество в схема-форме — бэк валидирует pydantic-схемой, любую самодеятельность отвергает.
export const EDITABLE_REPORT_SPEC = {
  foodcost: {
    scalars: [{ key: 'assistant_summary', label: 'Итоговый вывод' }],
    lists: [{
      key: 'measures', label: 'Меры оптимизации', titleField: 'title',
      fields: [
        { key: 'title', label: 'Заголовок' },
        { key: 'justification', label: 'Обоснование', multiline: true },
        { key: 'compensation', label: 'Компенсация' },
      ],
    }],
  },
  abc: {
    scalars: [{ key: 'assistant_summary', label: 'Итоговый вывод' }],
    lists: [
      { key: 'positions', label: 'Позиции', titleField: 'name',
        fields: [{ key: 'comment', label: 'Комментарий', multiline: true }] },
      { key: 'missing_data', label: 'Недостаточно данных', titleField: 'name',
        fields: [{ key: 'reason', label: 'Причина' }] },
    ],
  },
  inventory: {
    scalars: [{ key: 'assistant_summary', label: 'Итоговый вывод' }],
    lists: [
      { key: 'diagnoses', label: 'Диагностика', titleField: 'item',
        fields: [
          { key: 'diagnosis', label: 'Диагноз' },
          { key: 'recommendation', label: 'Рекомендация', multiline: true },
          { key: 'data_needed', label: 'Нужные данные' },
        ] },
      { key: 'top_actions', label: 'План действий', titleField: 'action',
        fields: [{ key: 'action', label: 'Действие', multiline: true }] },
    ],
  },
};

// Собрать тело PATCH {result:{…}} из отредактированного СЫРОГО result: только editable-
// поля, списки — все элементы (длину менять нельзя, бэк сверяет). null если тип без спеки.
export function toResultPatch(type, raw) {
  const spec = EDITABLE_REPORT_SPEC[type];
  if (!spec || !raw) return null;
  const out = {};
  for (const s of spec.scalars) out[s.key] = raw[s.key] ?? '';
  for (const l of spec.lists) {
    const arr = Array.isArray(raw[l.key]) ? raw[l.key] : [];
    out[l.key] = arr.map((item) => {
      const o = {};
      for (const f of l.fields) o[f.key] = (item && item[f.key]) ?? '';
      return o;
    });
  }
  return out;
}

// ── reportsReal — fakeApi-совместимый сёрфейс ──
async function listReports(filter = {}) {
  const params = {};
  if (filter.type) params.type = filter.type;
  if (filter.subject_kind) params.subject_kind = filter.subject_kind;
  if (filter.subject_id != null && filter.subject_id !== '') params.subject_id = filter.subject_id;
  const data = await unwrap(api.listReports(params));
  let rows = asArray(data).map(adaptBrief);
  const entity = (filter.entity || '').trim().toLowerCase();
  if (entity) {
    rows = rows.filter((r) => (r.title || '').toLowerCase().includes(entity) || (r.summary || '').toLowerCase().includes(entity));
  }
  return rows;
}

async function getReport(id) {
  return adaptDetail(await unwrap(api.getReport(id)));
}

async function deleteReport(id) {
  return unwrap(api.deleteReport(id));
}

// PATCH /api/reports/<id>/ {title?, summary?, result?}. Для result передавать СХЕМА-форму
// editable-подмножества (см. toResultPatch на result_raw) — НЕ адаптированный display-result.
// Бэк сливает её поверх хранимого result и валидирует pydantic-схемой (вычисляемые поля и
// чужие ключи отвергаются 400), так что испортить отчёт нельзя даже кривым телом.
async function updateReport(id, body) {
  return unwrap(api.updateReport(id, body));
}

export const reportsReal = { listReports, getReport, deleteReport, updateReport };
